// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSystemColor } from "@conklin/XDL/Utilities";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography";
import React, { FC } from "react";
import { css } from "styled-components";

const ANOTHER_PADDING_VALUE = "ANOTHER_PADDING_VALUE";

const enum fields {
    paddingSize = "paddingSize",
    paddingSizeInput = "paddingSizeInput",
    horizontalRule = "horizontalRule",
    hrHeight = "hrHeight",
    hrColor = "hrColor",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.paddingSize]: number | string;
        [fields.paddingSizeInput]: number;
        [fields.horizontalRule]: boolean;
        [fields.hrHeight]: number;
        [fields.hrColor]: string;
    };
}

type Props = OwnProps;

const getPaddingSize = (fields: Props["fields"]): number => {
    if (!fields.paddingSize) {
        return 0;
    }

    return fields.paddingSize !== ANOTHER_PADDING_VALUE ? Number(fields.paddingSize) : fields.paddingSizeInput;
};

const getHrHeight = (fields: Props["fields"]): number => {
    if (!fields.horizontalRule) {
        return 0;
    }

    const paddingSize = getPaddingSize(fields);
    let hrHeight = fields.hrHeight;
    if (!hrHeight || hrHeight < 1) {
        hrHeight = 1;
    } else if (hrHeight > paddingSize) {
        hrHeight = paddingSize;
    }

    return hrHeight;
};

const SpacerWidget: FC<Props> = ({ fields }) => {
    return (
        <Typography
            data-test-selector="spacer-widget"
            css={css`
                padding: ${getPaddingSize(fields)}px 0;
                display: flex;
                flex-direction: column;
                justify-content: center;

                ${fields.horizontalRule &&
                css`
                    &:after {
                        content: "";
                        display: block;
                        width: 100%;
                        height: ${getHrHeight(fields)}px;
                        background-color: ${fields.hrColor};
                    }
                `}

                @media print {
                    display: block;
                    float: left;
                    width: 100%;
                }
            `}
        ></Typography>
    );
};

const widgetModule: WidgetModule = {
    component: SpacerWidget,
    definition: {
        displayName: "Spacer",
        group: "Xngage" as any,
        fieldDefinitions: [
            {
                name: fields.paddingSize,
                displayName: "Padding Size",
                editorTemplate: "DropDownField",
                hideEmptyOption: true,
                options: [
                    {
                        displayName: "0",
                        value: "0",
                    },
                    {
                        displayName: "25px",
                        value: "25",
                    },
                    {
                        displayName: "50px",
                        value: "50",
                    },
                    {
                        displayName: "75px",
                        value: "75",
                    },
                    {
                        displayName: "100px",
                        value: "100",
                    },
                    {
                        displayName: "Another value",
                        value: ANOTHER_PADDING_VALUE,
                    },
                ],
                defaultValue: "25",
                fieldType: "General",
            },
            {
                name: fields.paddingSizeInput,
                displayName: "Another Padding Size",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                min: 0,
                max: 100,
                fieldType: "General",
                placeholder: "Enter padding style",
                isVisible: (item: HasFields) => item.fields.paddingSize === ANOTHER_PADDING_VALUE,
            },
            {
                name: fields.horizontalRule,
                displayName: "Show Horizontal Rule",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.hrColor,
                displayName: "Rule Color",
                editorTemplate: "ColorPickerField",
                defaultValue: getSystemColor("common.border"),
                fieldType: "General",
                isVisible: (item: HasFields) => item.fields.horizontalRule,
                popoverLocation: "top",
            },
            {
                name: fields.hrHeight,
                displayName: "Rule Height",
                editorTemplate: "IntegerField",
                defaultValue: 1,
                fieldType: "General",
                placeholder: "ُEnter rule height in px",
                isRequired: true,
                min: 1,
                validate: (value: number | null, item: HasFields) => {
                    const paddingSize = getPaddingSize(item.fields as Props["fields"]);
                    if (!value || value > paddingSize) {
                        return `Max value is ${item.fields.paddingSize}`;
                    }
                    return null;
                },
                isVisible: (item: HasFields) => item.fields.horizontalRule,
            },
        ],
    },
};

export default widgetModule;
